import { type InputHTMLAttributes, useCallback } from 'react'
import z from 'zod'
import { TextInput } from './text-input.tsx'

export const RangeInputSizeSchema = z.enum(['small', 'medium'])
export type RangeInputSize = z.infer<typeof RangeInputSizeSchema>

export type RangeInputProps = {
  /** Current minimum value (null = empty) */
  minValue: number | null
  /** Current maximum value (null = empty) */
  maxValue: number | null
  /** Callback fired when minimum value changes */
  onMinChange: (value: number | null) => unknown
  /** Callback fired when maximum value changes */
  onMaxChange: (value: number | null) => unknown
  /** Minimum allowed value in the range */
  min: number
  /** Maximum allowed value in the range */
  max: number
  /** Label text for accessibility */
  label: string
  /** Size of the inputs */
  size?: RangeInputSize
  /** Whether the inputs are disabled */
  disabled?: boolean
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'value' | 'type' | 'min' | 'max'>

export const RangeInput: React.FC<RangeInputProps> = ({
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  min,
  max,
  label,
  size = 'small',
  disabled,
  ...props
}) => {
  const handleMinChange = useCallback(
    (value: string) => {
      const numValue = value === '' ? null : Number(value)

      if (numValue !== null && (numValue < min || numValue > max)) {
        return
      }

      if (numValue !== null && maxValue !== null && numValue > maxValue) {
        return
      }

      onMinChange(numValue)
    },
    [min, max, maxValue, onMinChange]
  )

  const handleMaxChange = useCallback(
    (value: string) => {
      const numValue = value === '' ? null : Number(value)

      if (numValue !== null && (numValue < min || numValue > max)) {
        return
      }

      if (numValue !== null && minValue !== null && numValue < minValue) {
        return
      }

      onMaxChange(numValue)
    },
    [min, max, minValue, onMaxChange]
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <TextInput
        type="number"
        label="From"
        value={minValue !== null ? String(minValue) : ''}
        onChange={handleMinChange}
        size={size}
        ariaLabel={`${label} minimum`}
        disabled={disabled}
        min={min}
        max={max}
        {...props}
      />
      <TextInput
        type="number"
        label="To"
        value={maxValue !== null ? String(maxValue) : ''}
        onChange={handleMaxChange}
        size={size}
        ariaLabel={`${label} maximum`}
        disabled={disabled}
        min={min}
        max={max}
        {...props}
      />
    </div>
  )
}
